import * as React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, KeyRound, LogOut, Menu, Moon, PanelLeft, Search, Sun, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { CommandPalette, isSearchShortcut } from "@/app/CommandPalette";
import { toggleSearch, type ShellDialog } from "@/app/dialogs";
import { NAV_GROUPS, badgeClearedAt, findRoute, type NavItem } from "@/app/nav";
import { useTheme } from "@/app/theme";
import { countName, NavCount } from "@/app/NavCount";
import { useBadges } from "@/app/useBadges";
import { useAuth } from "@/auth/AuthProvider";
import { ChangePasswordModal } from "@/auth/ChangePasswordModal";
import { Button } from "@/components/ui/button";

/**
 * The panel: a permanently dark sidebar of collapsible groups, a 56px top bar,
 * and the routed screen under it.
 *
 * THE SIDEBAR DOES NOT TURN WITH THE THEME. That is the design's defining move
 * -- one silhouette whether the operator runs light or dark -- and it is why
 * `--sidebar-accent` exists as its own token: the brand colour is ink in the
 * light theme and would be invisible on a bar that is always dark.
 */
const RAIL_KEY = "ewn.admin.sidebar-rail";
const DESKTOP = "(min-width: 768px)";
const NARROW = "(max-width: 767px)";

/**
 * A media query as React state.
 *
 * Feature-detected rather than assumed: jsdom ships no `matchMedia`, and a
 * layout hook is not worth taking the whole panel down in a test environment.
 * The fallback leaves the sidebar REACHABLE rather than inert.
 */
function useMedia(query: string, fallback: boolean): boolean {
  const subscribe = React.useCallback(
    (cb: () => void) => {
      const mql = window.matchMedia?.(query);
      if (!mql) return () => {};
      mql.addEventListener("change", cb);
      return () => mql.removeEventListener("change", cb);
    },
    [query],
  );
  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia?.(query).matches ?? fallback,
    () => fallback,
  );
}

export function Shell() {
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const { state, signOut } = useAuth();

  const isDesktop = useMedia(DESKTOP, true);
  const isNarrow = useMedia(NARROW, false);

  const [railPref, setRailPref] = React.useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "1";
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(RAIL_KEY, railPref ? "1" : "0");
    } catch {
      /* The choice still holds for this page load. */
    }
  }, [railPref]);
  // The rail is a desktop idea. On a phone the same <aside> is a drawer and
  // always shows its labels, so the markup differs rather than just the width.
  const rail = railPref && isDesktop;

  const [drawer, setDrawer] = React.useState(false);
  const [dialog, setDialog] = React.useState<ShellDialog>(null);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isSearchShortcut(e)) {
        e.preventDefault();
        setDialog(toggleSearch);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const closeDialog = React.useCallback(() => setDialog(null), []);
  React.useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawer(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer]);
  React.useEffect(() => {
    setDrawer(false);
  }, [location.pathname]);

  const here = findRoute(location.pathname);
  const badges = useBadges(location.key, badgeClearedAt(location.pathname, location.search));
  const activeGroupId = here?.group.id ?? null;

  /**
   * One group open at a time, and it follows the route: a deep link never lands
   * inside a closed menu with nothing on screen saying where it is.
   *
   * Deliberately NOT persisted. A stored toggle would be overwritten by the
   * active group on every load before anybody saw it, so the storage would be
   * ceremony rather than behaviour.
   */
  const [openGroup, setOpenGroup] = React.useState<string | null>(activeGroupId);
  React.useEffect(() => {
    if (activeGroupId) setOpenGroup(activeGroupId);
  }, [activeGroupId]);

  function renderLink(item: NavItem, isRail: boolean, hidden = false) {
    const Icon = item.icon;
    const count = item.badge && badges ? badges[item.badge] : 0;
    const railName = countName(item.label, count);
    return (
      <NavLink
        to={item.to}
        end={item.end}
        // The markup stays mounted inside a closed group so the collapse can
        // animate, which means it has to be taken out of the tab order by hand.
        tabIndex={hidden ? -1 : undefined}
        title={isRail ? railName : undefined}
        aria-label={isRail ? railName : undefined}
        className={({ isActive }) =>
          cn(
            "relative flex items-center rounded text-sm transition",
            // The focus ring in here is `sidebar-accent`, not `ring`: the theme
            // ring is ink in light mode and the bar is always dark, so the
            // whole menu lost its focus indicator when tabbed through.
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent",
            isRail ? "h-10 w-10 justify-center" : "gap-3 py-2 pl-9 pr-3",
            isActive
              ? cn(
                  "bg-sidebar-active text-sidebar-text-active",
                  // The rail reads as "you are here" against the menu's left
                  // edge. Inside a 40px icon button it is just a stripe.
                  !isRail &&
                    "before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-r before:bg-sidebar-accent",
                )
              : "text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-hover",
          )
        }
      >
        <Icon aria-hidden className="h-4 w-4 shrink-0" />
        {!isRail && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
        <NavCount count={count} rail={isRail} />
      </NavLink>
    );
  }

  return (
    <div className="flex h-full">
      {/* Fourteen links precede the content on every route and there has to be
          a way past them (WCAG 2.4.1). Visible only on focus, so it costs the
          layout nothing until a keyboard user needs it. */}
      <a
        href="#main"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById("main");
          main?.focus();
          main?.scrollIntoView?.();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[80] focus:rounded focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-card focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to content
      </a>

      {drawer && (
        <div
          className="fixed inset-0 z-30 bg-backdrop/50 backdrop-blur-[2px] md:hidden"
          onClick={() => setDrawer(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex flex-col bg-sidebar transition-[width,transform] duration-200 ease-[cubic-bezier(.2,0,0,1)]",
          "md:static md:translate-x-0",
          drawer ? "translate-x-0" : "-translate-x-full",
          rail ? "w-sidebar-rail" : "w-sidebar",
        )}
        // Off-canvas is not "hidden": a closed drawer kept every link in the
        // tab order, so the first fourteen Tab stops on a phone were
        // off-screen. `inert` takes the subtree out of focus AND out of the
        // accessibility tree, and only below md where it really is off-canvas.
        {...(!drawer && isNarrow ? { inert: "" as const } : {})}
      >
        <div
          className={cn(
            "flex h-topbar shrink-0 items-center gap-2.5 border-b border-sidebar-border",
            rail ? "justify-center px-0" : "px-4",
          )}
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-sidebar-accent text-sm font-bold text-sidebar-bg">
            e
          </span>
          {!rail && <span className="truncate font-semibold tracking-tight text-sidebar-text-active">eSIM with Number</span>}
          {/* The drawer slides over the hamburger that opened it, so without
              this there is no control to close it -- only a tap on whatever
              strip of backdrop is still showing. */}
          <button
            type="button"
            onClick={() => setDrawer(false)}
            aria-label="Close menu"
            className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded text-sidebar-text transition hover:bg-sidebar-hover hover:text-sidebar-text-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent md:hidden"
          >
            <X aria-hidden className="h-[18px] w-[18px]" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2" aria-label="Admin">
          {NAV_GROUPS.map((group, index) => {
            const open = openGroup === group.id;

            // The rail drops the group chrome entirely. A collapsed accordion
            // at 64px would be four chevrons you cannot label -- a hairline
            // between runs of icons carries the same grouping with none of the
            // machinery.
            if (rail) {
              return (
                <div key={group.id}>
                  {index > 0 && <div className="mx-3 my-1.5 h-px bg-sidebar-border" />}
                  <ul className="space-y-0.5 px-2">
                    {group.items.map((item) => (
                      <li key={item.to}>{renderLink(item, true)}</li>
                    ))}
                  </ul>
                </div>
              );
            }

            return (
              <div key={group.id} className="px-2">
                <button
                  type="button"
                  onClick={() => setOpenGroup((cur) => (cur === group.id ? null : group.id))}
                  aria-expanded={open}
                  aria-controls={`navgroup-${group.id}`}
                  className={cn(
                    "flex w-full items-center gap-3 rounded px-3 py-2 text-sm font-medium transition",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent",
                    open
                      ? "text-sidebar-text-active"
                      : "text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-hover",
                  )}
                >
                  <group.icon aria-hidden className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
                  <ChevronDown
                    aria-hidden
                    className={cn("h-3.5 w-3.5 shrink-0 transition-transform duration-200", open && "rotate-180")}
                  />
                </button>

                {/* 0fr to 1fr on grid-template-rows: the browser animates to
                    the content's natural height with no JS measurement and no
                    layout thrash. The row must be min-h-0 or the grid refuses
                    to shrink. */}
                <div
                  id={`navgroup-${group.id}`}
                  aria-hidden={!open}
                  style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
                  className="grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(.2,0,0,1)]"
                >
                  <ul className="min-h-0 space-y-0.5 overflow-hidden">
                    {group.items.map((item) => (
                      // aria-hidden hides a closed group from a screen reader
                      // and tabIndex -1 takes it out of the tab order. One
                      // without the other leaves a link that is announced but
                      // unreachable, or reachable but unannounced.
                      <li key={item.to}>{renderLink(item, false, !open)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-card px-3 md:px-4">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setDrawer(true)} aria-label="Open menu">
            <Menu aria-hidden className="h-[18px] w-[18px]" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            onClick={() => setRailPref((v) => !v)}
            aria-label={railPref ? "Expand the menu" : "Collapse the menu"}
            aria-pressed={railPref}
          >
            <PanelLeft aria-hidden className="h-[18px] w-[18px]" />
          </Button>

          <div className="min-w-0 flex-1 truncate text-sm font-medium">{here?.item.label ?? ""}</div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDialog("search")}
            aria-label="Search (Ctrl+K)"
            title="Search (Ctrl+K)"
          >
            <Search aria-hidden className="h-[18px] w-[18px]" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to the light theme" : "Switch to the dark theme"}
          >
            {theme === "dark" ? (
              <Sun aria-hidden className="h-[18px] w-[18px]" />
            ) : (
              <Moon aria-hidden className="h-[18px] w-[18px]" />
            )}
          </Button>

          {/* The address Access asserted, shown where an operator will see it.
              This is how somebody notices they are signed in on a colleague's
              laptop before they approve an order as that colleague. */}
          <span className="hidden max-w-[220px] truncate text-xs text-muted-foreground sm:inline">
            {state?.email}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDialog("password")}
            aria-label="Change your password"
            title="Change your password"
          >
            <KeyRound aria-hidden className="h-[18px] w-[18px]" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => void signOut()} aria-label="Sign out">
            <LogOut aria-hidden className="h-[18px] w-[18px]" />
          </Button>
        </header>

        <main id="main" tabIndex={-1} className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
        <ChangePasswordModal open={dialog === "password"} onClose={closeDialog} />
        <CommandPalette open={dialog === "search"} onClose={closeDialog} />
      </div>
    </div>
  );
}
